/* eslint-disable @typescript-eslint/no-explicit-any */
import { getIn } from 'final-form';
import { clone, equals } from 'ramda';

type FieldRef = {
  form: any;
  fieldPath: string;
};

// Путь до нарушения в решении КНМ, ПМ или ПР
const DECISION_VIOLATION_PATH_REGEXP = /^(pmResultDecisions|resultDecisions|organization\.resultDecisions)\[\d+\]\.(violatedMandatoryRequirements|eliminatedMandatoryRequirements)\[\d+\]\.violatedMandatory\[\d+\]\.violations\[\d+\]$/;

class ViolationSyncRegistry {
  private registry = new Map<string, Set<FieldRef>>();

  private syncingFrom = new Set<string>();

  register(violationId: string, fieldRef: FieldRef) {
    if (!this.registry.has(violationId)) {
      this.registry.set(violationId, new Set());
    }
    this.registry.get(violationId)!.add(fieldRef);
  }

  unregister(violationId: string, fieldRef: FieldRef) {
    const refs = this.registry.get(violationId);

    if (refs) {
      refs.delete(fieldRef);
      if (refs.size === 0) this.registry.delete(violationId);
    }
  }

  syncField(violationId: string, sourceFieldPath: string, propertyPath: string, newValue: any) {
    const refs = this.registry.get(violationId);

    if (!refs) return;

    const originKey = `${violationId}:${sourceFieldPath}`;

    if (this.syncingFrom.has(originKey)) return;

    this.syncingFrom.add(originKey);

    try {
      refs.forEach(ref => {
        if (ref.fieldPath !== sourceFieldPath) {
          const fieldName = `${ref.fieldPath}.${propertyPath}`;

          ref.form.mutators.setFieldSilent(fieldName, newValue);
        }
      });
    } finally {
      setTimeout(() => this.syncingFrom.delete(originKey), 0);
    }
  }
}

export const violationSyncRegistry = new ViolationSyncRegistry();

/**
 * В отличие от violationSyncRegistry.syncField ищет копии нарушения по значениям формы, а не по отрисованным полям,
 * поэтому доходит и до нераскрытых решений.
 */
export const syncFieldAcrossDecisions = (
  form: any,
  sourceViolationFieldName: string,
  violationId: string | null | undefined,
  propertyPath: string,
) => {
  const match = sourceViolationFieldName.match(DECISION_VIOLATION_PATH_REGEXP);

  if (!form || !violationId || !match) return;

  const [, decisionsPath, requirementsKey] = match;
  const { values } = form.getState();
  const newValue = getIn(values, `${sourceViolationFieldName}.${propertyPath}`) ?? null;
  const decisions: any[] = getIn(values, decisionsPath) || [];

  decisions.forEach((decision, decisionIndex) => {
    decision?.[requirementsKey]?.forEach((npa: any, npaIndex: number) => {
      npa?.violatedMandatory?.forEach((mandatory: any, mandatoryIndex: number) => {
        mandatory?.violations?.forEach((violation: any, violationIndex: number) => {
          const violationFieldName = `${decisionsPath}[${decisionIndex}].${requirementsKey}[${npaIndex}].violatedMandatory[${mandatoryIndex}].violations[${violationIndex}]`;

          if (
            violation?.id !== violationId ||
            violationFieldName === sourceViolationFieldName ||
            equals(getIn(violation, propertyPath) ?? null, newValue)
          ) {
            return;
          }

          // Глубокая копия, чтобы решения не делили одни и те же объекты
          form.change(`${violationFieldName}.${propertyPath}`, clone(newValue));
        });
      });
    });
  });
};
